import { inject, Injectable } from '@angular/core';

import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import {
    GENERIC_RECORD_ACTION_TYPES,
    PORTAL_ICON_ACTIONS,
    PortalActionType
} from '../entities/actions/action-types.entity';

import { HttpErrorResponse } from '@angular/common/http';
import { GetDirectDownloadUserAction } from '@common/entities/actions/direct-download.action';
import {
    ActionItemFunction,
    ActionItemOptions,
    ActionsService,
    ActionTooltipHelper,
    asyncIconActionItemStreamHelper,
    DownloadsService,
    ExportLocationsService,
    ExportRequestService,
    ExportsService,
    GenericActionsSandbox,
    GenericSelectionService,
    GetContactAdminUserAction,
    GetExportPreviewUserAction,
    GetOpenInViewerUserAction,
    GetPermalink,
    GetShareUserAction,
    GetWatchUserAction,
    MoreActionsButton,
    PortalShareDialogComponent,
    PortalShareDialogConfig,
    SearchService,
    UserAction
} from '@mediahaven/shared';
import {
    ActionGroup,
    ActionGroupStyle,
    ActionItem,
    ActionItemStyle,
    ApiActionBundle,
    ApiFragment,
    ApplicationConfigService,
    DialogConfig,
    DialogRef,
    DialogStyle,
    ExportSource,
    IExportJob,
    NotificationType,
    ShDynamicDialogService,
    Toast
} from '@stylehaven/components';

@Injectable({
    providedIn: 'root'
})
export class ActionsSandbox extends GenericActionsSandbox {
    private readonly mapping: { [key in PortalActionType]?: ActionItemFunction<PortalActionType> } = {
        [PortalActionType.CONTACT_ADMIN]: this.getContactAdminActionItem,
        [PortalActionType.DOWNLOAD_PREVIEW]: this.getDownloadPreviewActionItem,
        [PortalActionType.DOWNLOAD]: this.getDirectDownLoadActionItem,
        [PortalActionType.OPEN_IN_VIEWER]: this.getOpenInViewerActionItem,
        [PortalActionType.SHARE]: this.getShareActionItem,
        [PortalActionType.WATCH]: this.getWatchActionItem
    };

    private readonly exportLocationService: ExportLocationsService = inject(ExportLocationsService);
    private readonly exportRequestService: ExportRequestService = inject(ExportRequestService);
    private readonly exportsService: ExportsService = inject(ExportsService);
    private readonly selectionService: GenericSelectionService = inject(GenericSelectionService);

    constructor(
        private readonly configService: ApplicationConfigService,
        private readonly downloadsService: DownloadsService,
        protected actionsService: ActionsService,
        protected dialogService: ShDynamicDialogService,
        protected searchService: SearchService,
        protected tooltipHelper: ActionTooltipHelper
    ) {
        super(actionsService, dialogService, tooltipHelper, searchService);
    }

    public getActionsAsSingle(
        actionTypes: readonly PortalActionType[],
        fragment: ApiFragment,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionGroup> {
        const actionItems$: Observable<ActionItem[]> = this.getActionsJoined(actionTypes, fragment, actionItemOptions);
        return actionItems$.pipe(
            map(actionItems => (actionItems.length ? [MoreActionsButton(actionItems)] : [])),
            map(actions => ({ actions: actions }))
        );
    }

    public getRegularActionsWithIconActions(
        actionTypes: readonly string[],
        fragment: ApiFragment,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionGroup> {
        const iconActionTypes: string[] = actionTypes.filter(type =>
            PORTAL_ICON_ACTIONS.includes(type as PortalActionType)
        );
        const nonIconActionTypes: string[] = actionTypes.filter(type =>
            GENERIC_RECORD_ACTION_TYPES.includes(type as PortalActionType)
        );

        const iconActions: Observable<ActionItem | null>[] = this.getActions(
            iconActionTypes as PortalActionType[],
            fragment,
            { ...actionItemOptions, withIcon: true }
        );

        const nonIconActions: Observable<ActionItem | null>[] = this.getActions(
            nonIconActionTypes as PortalActionType[],
            fragment,
            actionItemOptions
        );
        const apiActions: Observable<ApiActionBundle> = this.getApiActionsAsBundle(fragment);

        return asyncIconActionItemStreamHelper(iconActions, nonIconActions, apiActions).pipe(
            map(actions => ({ actions, style: ActionGroupStyle.Collapsed }))
        );
    }

    public getActionsJoined(
        actionTypes: readonly PortalActionType[],
        fragment: ApiFragment,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem[]> {
        const apiActionItems$: Observable<(ActionItem | null)[]> = this.getApiActions(fragment);

        const baseActionItems: Observable<ActionItem | null>[] = this.getActions(
            actionTypes,
            fragment,
            actionItemOptions
        );

        return this.combineActionStreams(baseActionItems, apiActionItems$);
    }

    private getActions(
        actionTypes: readonly PortalActionType[],
        fragment: ApiFragment,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem | null>[] {
        return actionTypes.map(actionType => this.getActionItem(actionType, fragment, actionItemOptions));
    }

    public getActionItem(
        actionType: PortalActionType,
        fragment: ApiFragment,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem | null> {
        const action: ActionItemFunction<PortalActionType> | undefined = this.mapping[actionType];
        if (typeof action === 'undefined') {
            return of(null);
        }
        return action.call(this, fragment, actionType, actionItemOptions);
    }

    private getWatchActionItem(
        fragment: ApiFragment,
        action: PortalActionType,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem | null> {
        const userAction: UserAction = GetWatchUserAction(fragment);

        const label: string = this.translateService.instant('actions.view-metadata');

        if (!userAction.isAvailable()) {
            return of(null);
        }

        const state: any = {
            queryIteratorStack: actionItemOptions.queryIteratorStack
        };

        return of({
            label: this.translateService.instant(label),
            navigationLink: {
                route: actionItemOptions.shareContext
                    ? fragment.getShareDetailUrl(actionItemOptions.shareContext)
                    : fragment.getDetailUrl(),
                extras: { state }
            },
            disabled: userAction.isDisabled(),
            disabledInfoMsg: this.tooltipHelper.getActionTooltip(userAction, [fragment])
        });
    }

    private getShareActionItem(
        fragment: ApiFragment,
        action: PortalActionType,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem | null> {
        const userAction: UserAction = GetShareUserAction(fragment, undefined, !!actionItemOptions.shareContext, true);

        if (!userAction.isAvailable()) {
            return of(null);
        }

        const icon: string | undefined =
            fragment.isPublished() && actionItemOptions.withIcon ? 'share-alt-square' : undefined;
        const style = icon ? ActionItemStyle.PureIconButton : undefined;

        return of({
            label: this.translateService.instant('actions.share'),
            eventListener: this.onShare.bind(this, fragment, action),
            disabled: userAction.isDisabled(),
            disabledInfoMsg: this.tooltipHelper.getActionTooltip(userAction, [fragment]),
            icon,
            style
        });
    }

    private getOpenInViewerActionItem(
        record: ApiFragment,
        action: PortalActionType,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem | null> {
        const userAction: UserAction = GetOpenInViewerUserAction(record);

        if (!userAction.isAvailable()) {
            return of(null);
        }

        const label: string = this.translateService.instant('actions.open-in-viewer');
        const baseUrl: string | undefined = this.configService.getInstant('baseUrl');

        if (!baseUrl) {
            console.warn('baseUrl was empty at this point');
            return of(null);
        }
        const urlParts: string[] = [baseUrl];
        if (!baseUrl.endsWith('/')) {
            urlParts.push('/');
        }

        if (actionItemOptions.shareContext) {
            urlParts.push('shared/', actionItemOptions.shareContext, '/');
        }

        urlParts.push('viewer/', record.getFragmentId());
        const externalUrl: string = urlParts.join('');
        return of({
            label,
            navigationLink: {
                route: externalUrl,
                target: '_blank'
            },
            disabled: userAction.isDisabled(),
            disabledInfoMsg: this.tooltipHelper.getActionTooltip(userAction, [record])
        });
    }

    private async onDownloadPreview(fragment: ApiFragment): Promise<void> {
        return this.downloadsService.downloadFragmentPreview(fragment).catch(error => this.downloadPreviewError(error));
    }

    public downloadPreviewError(error: Error): void {
        console.error(error);
        this.createAndAddNewToast('action-toasts.download-preview.error', NotificationType.Error);
    }

    private onDirectDownload(mainRecord: ApiFragment, action: PortalActionType, exportRecords: ApiFragment[]): void {
        const actionBusyToast: Toast = this.createActionBusyToast(
            'action-toasts.download-fragment-request',
            mainRecord.getMh2Entity()
        );

        this.addToast(actionBusyToast);

        const exportRequest$ = this.exportLocationService
            .getDefaultExportLocation()
            .pipe(
                map(exportLocation =>
                    this.exportRequestService.exportLocationToExportRequest(
                        exportLocation,
                        exportRecords,
                        ExportSource.ORIGINAL
                    )
                )
            );

        const export$ = exportRequest$.pipe(
            switchMap(exportRequest => this.exportsService.exportRecords(exportRequest))
        );

        export$
            .pipe(
                switchMap(([results]: IExportJob[]) => this.exportsService.getDownloadStatus(results.ExportJobId)),
                tap(() => this.closeToast(actionBusyToast.id)),
                tap(() => this.downloadFragmentRequestSuccess(mainRecord)),
                tap((output: IExportJob) =>
                    output.DownloadUrl ? this.downloadsService.downloadUrl(output.DownloadUrl) : null
                ),
                tap(() => this.emitActionPerformed(action, mainRecord, true, false)),
                catchError(error => {
                    this.closeToast(actionBusyToast.id);
                    this.emitActionPerformed(action, mainRecord, false, false);
                    this.downloadFragmentRequestFailure(error, mainRecord);
                    return of(error);
                }),
                take(1)
            )
            .subscribe();
    }

    private downloadFragmentRequestSuccess(record: ApiFragment): void {
        const translateKey: string = 'action-toasts.download-fragment-request';
        this.createAndAddActionSuccessToast(translateKey, record.getMh2Entity(), 1, []);
    }

    private downloadFragmentRequestFailure(error: HttpErrorResponse, record: ApiFragment): void {
        console.error('Exporting fragment failed', error);
        this.createAndAddActionErrorToast('action-toasts.download-fragment-request', record.getMh2Entity(), 1);
    }

    private getDownloadPreviewActionItem(
        fragment: ApiFragment,
        action: PortalActionType,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem | null> {
        const userAction: UserAction = GetExportPreviewUserAction(fragment);

        if (!userAction.isAvailable()) {
            return of(null);
        }

        const icon: string | undefined = fragment.isPublished() && actionItemOptions.withIcon ? 'download' : undefined;

        const style = icon ? ActionItemStyle.PureIconButton : undefined;

        return of({
            label: this.translateService.instant('actions.download.download-preview'),
            eventListener: this.onDownloadPreview.bind(this, fragment),
            disabled: userAction.isDisabled(),
            disabledInfoMsg: this.tooltipHelper.getActionTooltip(userAction, [fragment]),
            icon,
            style
        });
    }

    private getDirectDownLoadActionItem(
        record: ApiFragment,
        action: PortalActionType,
        actionItemOptions: ActionItemOptions
    ): Observable<ActionItem | null> {
        let exportRecords$: Observable<ApiFragment[]>;
        if (record.isSelection()) {
            exportRecords$ = this.selectionService.getAllExportableSelectionFragments(record.getFragmentId());
        } else {
            exportRecords$ = of([record]);
        }

        const userAction$: Observable<UserAction> = this.exportLocationService
            .getDefaultExportLocation()
            .pipe(map(downloadableLocation => GetDirectDownloadUserAction(record, downloadableLocation)));

        const icon: string | undefined =
            record.isPublished() && actionItemOptions.withIcon ? 'cloud-download-alt' : undefined;
        const style = icon ? ActionItemStyle.PureIconButton : undefined;

        return combineLatest([userAction$, exportRecords$]).pipe(
            map(([userAction, exportRecords]) => {
                if (!userAction.isAvailable()) {
                    return null;
                }

                const hasExportableRecords = exportRecords.length !== 0;

                const tooltip = !hasExportableRecords
                    ? this.translate('action-tooltips.selection.no-exportable.generic')
                    : this.tooltipHelper.getActionTooltip(userAction, exportRecords);

                return {
                    label: this.translateService.instant('actions.download.main'),
                    eventListener: () => this.onDirectDownload(record, action, exportRecords),
                    disabled: userAction.isDisabled() || !hasExportableRecords,
                    disabledInfoMsg: tooltip,
                    icon,
                    style
                };
            })
        );
    }

    private onShare(fragment: ApiFragment, action: PortalActionType): void {
        const title: string = this.translateService.instant('actions.share');
        const ok: string = this.translate('actions.copy-to-clipboard');
        const cancel: string = this.translateService.instant('actions.common.cancel');

        const permalink: string = GetPermalink(fragment, this.configService.getInstant('permalinkBase'));

        const config: DialogConfig<PortalShareDialogConfig> = {
            title,
            data: {
                url: permalink,
                cancel,
                ok,
                record: fragment,
                shareUrl: true,
                shareSocial: true,
                permalink
            },
            options: {
                style: DialogStyle.Wide
            }
        };

        const ref: DialogRef<boolean> = this.dialogService.open(PortalShareDialogComponent, config);

        ref.afterClosed$.pipe(take(1)).subscribe(copySuccessful => {
            if (copySuccessful) {
                this.copyLinkSuccess();
                this.emitActionPerformed(action, fragment, true);
            } else {
                this.copyLinkFailure();
                this.emitActionPerformed(action, fragment, false);
            }
        });
    }

    public copyLinkSuccess(): void {
        this.createAndAddNewToast('action-toasts.copy-link.success', NotificationType.Success);
    }

    public copyLinkFailure(): void {
        this.createAndAddNewToast('action-toasts.copy-link.error', NotificationType.Error);
    }

    private getContactAdminActionItem(record: ApiFragment): Observable<ActionItem | null> {
        const email: string | undefined = this.configService.getInstant('adminEmail');
        const url: string | undefined = this.configService.getInstant('baseUrl');

        const userAction: UserAction = GetContactAdminUserAction(record, email, url);

        if (!email) {
            console.warn('adminEmail was empty at this point');
        }

        if (!url) {
            console.warn('baseUrl was empty at this point');
        }

        if (!userAction.isAvailable()) {
            return of(null);
        }

        return of({
            label: this.translate('actions.contact-admin'),
            eventListener: this.actionsService.sendEmail.bind(this, record, email, url),
            disabled: userAction.isDisabled(),
            disabledInfoMsg: this.tooltipHelper.getActionTooltip(userAction, [record])
        });
    }
}
